import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { industries } from "../../data/industries";
import { states, getCitiesByState } from "../../data/locations";
import { blogPosts } from "../../data/blogPosts";
import { getCategoryName } from "../../data/blogCategories";
import { business } from "../../config/business";

import heroImage from "../assets/hero-industrial.jpg";
import aboutImage from "../assets/building.jpg";
import industrialShed from "../assets/industrial-shed.jpg";
import fabrication from "../assets/mezzanine flooring.png";
import industrialStructure from "../assets/2.png";
import prefabBuilding from "../assets/3.png";
import ctaImage from "../assets/fabrication-hero.jpg";

import { FaWhatsapp } from "react-icons/fa";
import {
  MapPin,
  Factory,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  ArrowUpRight,
  Star,
  Quote,
  ClipboardList,
  PenTool,
  Hammer,
  Truck,
  Award,
  Users,
  CalendarClock,
} from "lucide-react";

import "@fontsource/montserrat/400.css";
import "@fontsource/montserrat/700.css";
import "@fontsource/inter/400.css";
import "@fontsource/inter/700.css";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const Reveal = ({ children, className }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    variants={fadeUp}
    className={className}
  >
    {children}
  </motion.div>
);

const yearsActive = new Date().getFullYear() - business.foundedYear;

const Homepage = () => {
  const services = [
    {
      title: "Industrial Sheds",
      description:
        "Heavy-duty sheds engineered for warehouses, factories, workshops, and industrial production centers.",
      image: industrialShed,
      link: "/industrial-sheds",
      featured: true,
    },
    {
      title: "Fabrication Services",
      description: "Custom MS/SS steel fabrication, structural frames, trusses and beams.",
      image: fabrication,
      link: "/FabricationServices",
    },
    {
      title: "Industrial Structures",
      description: "Large-scale frameworks for machinery, manufacturing and storage.",
      image: industrialStructure,
      link: "/IndustrialStructures",
    },
    {
      title: "Prefabricated Buildings",
      description: "Quick-install prefab units for offices, cabins, godowns and commercial spaces.",
      image: prefabBuilding,
      link: "/pre-fab-structure-buildings",
    },
  ];

  const stats = [
    { icon: CalendarClock, value: `${yearsActive}+`, label: "Years in Structural Engineering" },
    { icon: MapPin, value: `${states.length}`, label: "States Served — TG, AP & Karnataka" },
    { icon: Star, value: "5.0", label: "Average Client Rating" },
    { icon: Users, value: "100%", label: "In-House Design & Fabrication" },
  ];

  const process = [
    { icon: ClipboardList, title: "Consult & Assess", description: "Site visit, load requirements and budget alignment." },
    { icon: PenTool, title: "Design & Engineer", description: "Structural drawings and PEB engineering sign-off." },
    { icon: Hammer, title: "Fabricate", description: "In-house MS/SS fabrication with QC at every stage." },
    { icon: Truck, title: "Erect & Handover", description: "On-site erection, sheeting and final handover." },
  ];

  const whyUs = [
    "Premium MS, SS & GI steel sourced from certified suppliers",
    "Experienced engineers and fabricators on every project",
    "Structural designs tailored to your site and load needs",
    "Fixed timelines with regular progress updates",
    "Built for decades — not just to pass inspection",
    "Transparent, itemised pricing with no hidden costs",
  ];

  const testimonials = [
    { name: "Panjala Eshwar Goud", quote: "Good quality and on time delivery with best service. Thank you TR Structures." },
    { name: "Gautham KV", quote: "Quality of work and commitment is good, with reasonable price." },
    { name: "Sri Venkateshwara Industries", quote: "Best services, on time delivery." },
  ];

  const featuredIndustries = industries.slice(0, 8);
  const featuredPosts = blogPosts.slice(0, 3);

  return (
    <>
      {/* ==== HERO — asymmetric, left-aligned, floating stat strip ==== */}
      {/* No overflow-hidden here: the stat strip below intentionally overlaps
          past this section's bottom edge via translate-y-1/2, and clipping
          would cut its content off. */}
      <section className="relative bg-foreground">
        <link
          rel="preload"
          fetchPriority="high"
          as="image"
          href={heroImage}
          imageSrcSet={`${heroImage}?format=webp&width=600 600w, ${heroImage}?format=webp&width=1200 1200w, ${heroImage}?format=webp&width=1920 1920w`}
          imageSizes="100vw"
        />
        <picture>
          <source
            srcSet={`${heroImage}?format=webp&width=600 600w, ${heroImage}?format=webp&width=1200 1200w, ${heroImage}?format=webp&width=1920 1920w`}
            sizes="100vw"
            type="image/webp"
          />
          <img
            src={heroImage}
            alt="TR Structures - Leading Industrial Shed & Steel Fabrication Company in Hyderabad"
            className="absolute inset-0 w-full h-full object-cover object-center"
            fetchPriority="high"
            decoding="async"
            loading="eager"
            width="1920"
            height="1080"
          />
        </picture>
        <div
          className="absolute inset-0 bg-gradient-to-r from-foreground via-foreground/85 to-foreground/40"
          aria-hidden="true"
        />

        <div className="relative z-10 container mx-auto px-6 pt-28 pb-32 md:pt-36 md:pb-40">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 bg-primary/15 border border-primary/40 text-primary font-montserrat font-semibold text-sm tracking-wide px-4 py-1.5 rounded-full mb-6"
            >
              Established {business.foundedYear} · Hyderabad
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="text-4xl md:text-6xl font-extrabold mb-6 leading-[1.1] text-white"
            >
              Engineered PEB &amp; Industrial Structures,{" "}
              <span className="text-primary">Built to Perform</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-lg text-white/80 mb-10 max-w-lg"
            >
              Design, fabrication and erection for warehouses, factories and commercial
              structures — delivered end to end from our Hyderabad facility.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/enquiry" className="btn-primary text-center">
                Get Project Estimate
              </Link>
              <a
                href={business.whatsapp.href}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-3 rounded-lg font-montserrat font-semibold hover:bg-[#20bd5a] transition"
              >
                <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
              </a>
            </motion.div>
          </div>
        </div>

        {/* Floating stat strip overlapping hero/next-section boundary */}
        <div className="relative z-10 container mx-auto px-6">
          <div className="translate-y-1/2 grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-2xl overflow-hidden shadow-2xl">
            {stats.map((s) => (
              <div key={s.label} className="bg-card p-6 text-center">
                <s.icon className="text-primary mx-auto mb-2" size={24} aria-hidden="true" />
                <p className="font-montserrat text-2xl font-extrabold text-foreground">{s.value}</p>
                <p className="text-xs text-muted-foreground mt-1 leading-snug">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <main>
        {/* ==== ABOUT — image/text split ==== */}
        <Reveal className="pt-32 pb-20 md:pt-40 md:pb-28">
          <section className="container mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src={aboutImage}
                alt="TR Structures fabrication yard in Hyderabad"
                className="rounded-2xl shadow-xl w-full h-[380px] object-cover"
                loading="lazy"
              />
              <div className="hidden sm:flex absolute -bottom-6 -right-6 bg-primary text-white rounded-2xl shadow-xl p-6 flex-col items-center justify-center">
                <span className="font-montserrat text-3xl font-extrabold leading-none">{yearsActive}+</span>
                <span className="text-xs font-semibold mt-1">Years Strong</span>
              </div>
            </div>
            <div>
              <p className="text-primary font-montserrat font-bold tracking-wide mb-3">ABOUT TR STRUCTURES</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-5 leading-tight">
                Design and fabrication, under one roof
              </h2>
              <p className="text-muted-foreground text-lg mb-6 leading-relaxed">
                Since {business.foundedYear}, TR Structures has designed and fabricated pre-engineered
                buildings, industrial sheds and steel structures for clients across Telangana and
                Andhra Pradesh — engineered in-house and erected by our own crews.
              </p>
              <ul className="space-y-3 mb-8">
                {whyUs.slice(0, 4).map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="text-secondary mt-0.5 shrink-0" size={20} aria-hidden="true" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/about" className="inline-flex items-center gap-2 font-montserrat font-bold text-primary group">
                More About Us
                <ArrowRight className="group-hover:translate-x-1 transition-transform" size={18} aria-hidden="true" />
              </Link>
            </div>
          </section>
        </Reveal>

        {/* ==== SERVICES — bento layout ==== */}
        <Reveal className="py-20 bg-muted">
          <section>
            <div className="container mx-auto px-6">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
                <div>
                  <p className="text-primary font-montserrat font-bold tracking-wide mb-3">WHAT WE BUILD</p>
                  <h2 className="text-3xl md:text-4xl font-bold">Our Core Services</h2>
                </div>
                <p className="text-muted-foreground max-w-md">
                  From concept to completion — industrial infrastructure engineered around your operation.
                </p>
              </div>

              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                {services.map((service) => (
                  <motion.div
                    key={service.title}
                    variants={fadeUp}
                    className={`group relative rounded-2xl overflow-hidden shadow-lg card-hover ${
                      service.featured ? "md:row-span-2 min-h-[420px]" : "min-h-[200px]"
                    }`}
                  >
                    <img
                      src={service.image}
                      alt={`${service.title} - TR Structures Hyderabad`}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/30 to-transparent" />
                    <Link
                      to={service.link}
                      aria-label={`Learn more about ${service.title}`}
                      className="relative z-10 flex flex-col justify-end h-full p-6 md:p-8"
                    >
                      <h3 className="font-montserrat text-xl md:text-2xl font-bold text-white mb-2">
                        {service.title}
                      </h3>
                      <p className={`text-white/80 mb-4 ${service.featured ? "max-w-md" : "max-w-xs text-sm"}`}>
                        {service.description}
                      </p>
                      <span className="inline-flex items-center gap-2 font-montserrat font-semibold text-primary">
                        Learn More
                        <ArrowUpRight
                          className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                          size={18}
                          aria-hidden="true"
                        />
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        </Reveal>

        {/* ==== PROCESS — horizontal timeline ==== */}
        <Reveal className="py-20">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-16">
                <p className="text-primary font-montserrat font-bold tracking-wide mb-3">HOW WE WORK</p>
                <h2 className="text-3xl md:text-4xl font-bold">From Site Visit to Handover</h2>
              </div>
              <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                <div
                  className="hidden lg:block absolute top-8 left-[12.5%] right-[12.5%] h-0.5 bg-border"
                  aria-hidden="true"
                />
                {process.map((step, i) => (
                  <div key={step.title} className="relative text-center">
                    <div className="relative z-10 w-16 h-16 mx-auto rounded-full bg-primary text-white flex items-center justify-center mb-5 shadow-lg">
                      <step.icon size={26} aria-hidden="true" />
                    </div>
                    <p className="text-xs font-montserrat font-bold text-secondary mb-1">
                      STEP {i + 1}
                    </p>
                    <h3 className="font-montserrat font-bold text-lg mb-2">{step.title}</h3>
                    <p className="text-muted-foreground text-sm">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== WHY CHOOSE US — dark band ==== */}
        <Reveal className="py-20 bg-foreground text-white">
          <section>
            <div className="container mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
              <div>
                <p className="text-primary font-montserrat font-bold tracking-wide mb-3">WHY TR STRUCTURES</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                  Built for decades, priced for today
                </h2>
                <p className="text-white/70 text-lg leading-relaxed">
                  We keep design, fabrication and erection in-house so quality stays consistent and
                  timelines stay predictable — from the first site visit to final handover.
                </p>
              </div>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
                {whyUs.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <Award className="text-primary mt-0.5 shrink-0" size={20} aria-hidden="true" />
                    <span className="text-white/90 text-sm md:text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </Reveal>

        {/* ==== INDUSTRIES — scrollable chip row ==== */}
        <Reveal className="py-20 bg-muted">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <p className="text-primary font-montserrat font-bold tracking-wide mb-3">SECTORS WE SERVE</p>
                <h2 className="text-3xl md:text-4xl font-bold">Built for Your Industry</h2>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-4 -mx-6 px-6 snap-x">
                {featuredIndustries.map((ind) => (
                  <Link
                    key={ind.slug}
                    to={`/industries/${ind.slug}`}
                    className="group shrink-0 snap-start flex items-center gap-3 bg-card border border-border rounded-full px-6 py-4 shadow-sm card-hover"
                  >
                    <Factory className="text-primary" size={20} aria-hidden="true" />
                    <span className="font-montserrat font-semibold text-foreground whitespace-nowrap">
                      {ind.name}
                    </span>
                  </Link>
                ))}
              </div>
              <div className="text-center mt-8">
                <Link to="/industries" className="btn-outline inline-block px-8 py-3">
                  View All Industries
                </Link>
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== TESTIMONIALS ==== */}
        <Reveal className="py-20">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <p className="text-primary font-montserrat font-bold tracking-wide mb-3">CLIENT FEEDBACK</p>
                <h2 className="text-3xl md:text-4xl font-bold">What Our Clients Say</h2>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {testimonials.map((t) => (
                  <div key={t.name} className="bg-card border border-border rounded-2xl p-8 shadow-sm">
                    <Quote className="text-primary mb-4" size={28} aria-hidden="true" />
                    <p className="text-foreground mb-6 leading-relaxed">&ldquo;{t.quote}&rdquo;</p>
                    <div className="flex items-center justify-between">
                      <p className="font-montserrat font-bold text-sm">{t.name}</p>
                      <div className="flex gap-0.5" aria-label="5 out of 5 stars">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} size={14} className="fill-secondary text-secondary" aria-hidden="true" />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== LOCATIONS ==== */}
        <Reveal className="py-20 bg-muted">
          <section>
            <div className="container mx-auto px-6">
              <div className="text-center mb-14">
                <p className="text-primary font-montserrat font-bold tracking-wide mb-3">WHERE WE DELIVER</p>
                <h2 className="text-3xl md:text-4xl font-bold">
                  {states.map((s) => s.name).join(" · ")}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
                {states.map((state) => {
                  const cities = getCitiesByState(state.slug).slice(0, 3);
                  return (
                    <div key={state.slug} className="bg-card rounded-2xl border border-border p-7">
                      <h3 className="font-montserrat font-bold text-xl text-foreground mb-4 flex items-center gap-2">
                        <MapPin className="text-secondary" size={20} aria-hidden="true" /> {state.name}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-5">
                        {cities.map((c) => (
                          <Link
                            key={c.slug}
                            to={`/locations/${state.slug}/${c.slug}`}
                            className="px-3 py-1 bg-muted rounded-full text-sm text-foreground hover:bg-primary hover:text-white transition"
                          >
                            {c.name}
                          </Link>
                        ))}
                      </div>
                      <Link to={`/locations/${state.slug}`} className="text-primary font-semibold text-sm hover:underline">
                        View all {state.name} locations →
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== RESOURCES — list, not another grid ==== */}
        <Reveal className="py-20">
          <section>
            <div className="container mx-auto px-6 max-w-4xl">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
                <div>
                  <p className="text-primary font-montserrat font-bold tracking-wide mb-3">LEARN MORE</p>
                  <h2 className="text-3xl md:text-4xl font-bold">Guides &amp; Resources</h2>
                </div>
                <Link to="/resources" className="text-primary font-montserrat font-bold hover:underline">
                  View All Resources →
                </Link>
              </div>
              <div className="divide-y divide-border border-t border-b border-border">
                {featuredPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/resources/${post.slug}`}
                    className="group flex items-center justify-between gap-6 py-6 hover:bg-muted/50 transition px-2"
                  >
                    <div>
                      <p className="text-xs text-secondary font-semibold mb-1 flex items-center gap-1">
                        <BookOpen size={12} aria-hidden="true" /> {getCategoryName(post.category)}
                      </p>
                      <h3 className="font-montserrat font-bold text-foreground">{post.title}</h3>
                    </div>
                    <ArrowRight
                      className="text-primary shrink-0 group-hover:translate-x-1 transition-transform"
                      size={20}
                      aria-hidden="true"
                    />
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </Reveal>

        {/* ==== FINAL CTA — split image/panel ==== */}
        <section className="relative overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <div className="relative min-h-[320px] lg:min-h-[420px]">
              <img
                src={ctaImage}
                alt="TR Structures active fabrication project"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-foreground/50" aria-hidden="true" />
            </div>
            <div className="bg-primary text-white flex items-center">
              <div className="p-10 md:p-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-5">Start Your Project With Confidence</h2>
                <p className="text-white/90 text-lg mb-8 max-w-md">
                  Partner with TR Structures for industrial buildings and fabrication — where quality
                  meets reliability.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/enquiry"
                    className="bg-white text-primary px-8 py-4 rounded-lg font-montserrat font-bold text-center hover:bg-gray-100 transition"
                  >
                    Send Enquiry Now
                  </Link>
                  <a
                    href={business.whatsapp.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-lg font-montserrat font-bold hover:bg-[#20bd5a] transition"
                  >
                    <FaWhatsapp size={20} aria-hidden="true" /> WhatsApp Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default Homepage;
